import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { sendMasterAIInstruction, createFromSuggestion, getClients, getBots } from '@/lib/api';
import { toast } from 'sonner';
import {
  Sparkles,
  Send,
  Bot,
  User,
  Loader2,
  Lightbulb,
  Network,
  Cpu,
  CheckCircle,
  Building2,
  ArrowRight
} from 'lucide-react';

const MasterAIPage = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: '¡Hola! Soy el Agente Maestro de Fluxo Co. Puedo ayudarte a:\n\n• Crear tenants y flujos automáticamente\n• Configurar flujos de atención\n• Generar prompts personalizados\n\nEscribe una instrucción como: "Crea un flujo para un taller mecánico que atienda clientes y cree tickets de servicio"'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const [stats, setStats] = useState({ clients: 0, bots: 0 });
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchStats = async () => {
    try {
      const [clientsRes, botsRes] = await Promise.all([
        getClients(),
        getBots()
      ]);
      setStats({
        clients: clientsRes.data.length,
        bots: botsRes.data.length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);
    setPendingSuggestion(null);

    try {
      const response = await sendMasterAIInstruction(userMessage);
      const data = response.data;

      let aiResponse = data.response;
      
      // Check if there are suggestions to display
      if (data.created_resources) {
        setPendingSuggestion(data.created_resources);
      }

      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse, suggestion: data.created_resources }]);
    } catch (error) {
      toast.error('Error al procesar la instrucción');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Lo siento, hubo un error al procesar tu instrucción. Por favor, intenta de nuevo.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateFromSuggestion = async () => {
    if (!pendingSuggestion) return;

    setIsLoading(true);
    try {
      const response = await createFromSuggestion(pendingSuggestion);
      const { admin_email, admin_password, access_url } = response.data;
      
      toast.success('¡Cliente y bot creados exitosamente!');
      setPendingSuggestion(null);
      fetchStats();
      
      let confirmationContent = '✅ ¡Perfecto! He creado el cliente y el bot según las sugerencias.';
      
      if (admin_email && admin_password) {
        confirmationContent += `\n\n🔑 **Credenciales de Acceso:**\n- **Usuario:** ${admin_email}\n- **Contraseña:** ${admin_password}\n- **URL:** ${access_url}\n\n*Por favor, guarda estos datos ahora.*`;
      } else {
        confirmationContent += ' Puedes verlos en las secciones de Clientes y Bots.';
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: confirmationContent
      }]);
    } catch (error) {
      toast.error('Error al crear los recursos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const examplePrompts = [
    'Crea un bot para un taller mecánico que atienda clientes y cree tickets de servicio',
    'Configura un asistente para una cafetería que tome pedidos por WhatsApp',
    'Necesito un bot para una clínica dental que agende citas',
    'Crea un bot de atención para una tienda de ropa online'
  ];

  return (
    <div className="space-y-6" data-testid="master-ai-page">
      {/* Header V4.3 - ADN Fluxo Co */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 pt-4">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
          <div className="flex items-center gap-2 mb-3">
            <Cpu className="w-5 h-5 text-blue-500" />
            <span className="text-[10px] uppercase tracking-[0.4em] font-black text-blue-500/80">Cerebro Central</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-white font-outfit">
            Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-emerald-400">Master AI</span>
          </h1>
          <p className="text-slate-500 mt-2 font-medium italic text-sm">Orquestación de inteligencia superior para configuración de ecosistemas conversacionales</p>
        </motion.div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-[#0F172A]/40 backdrop-blur-xl border border-white/5">
            <Building2 className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-bold text-white/90">{stats.clients} <span className="text-slate-500 font-medium">Clients</span></span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-[#0F172A]/40 backdrop-blur-xl border border-white/5">
            <Bot className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-bold text-white/90">{stats.bots} <span className="text-slate-500 font-medium">Bots</span></span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Chat area */}
        <div className="lg:col-span-3">
          <Card className="h-[calc(100vh-16rem)]">
            <CardContent className="p-0 h-full flex flex-col">
              {/* Messages */}
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {message.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-white" />
                        </div>
                      )}
                      
                      <div className={message.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-ai'}>
                        <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                        
                        {/* Suggestion card */}
                        {message.suggestion && (
                          <div className="mt-4 p-4 rounded-lg bg-background/50 border border-white/10 space-y-3">
                            <p className="text-xs font-medium text-muted-foreground">Sugerencia generada:</p>
                            
                            {message.suggestion.suggested_client && (
                              <div className="p-3 rounded-lg bg-muted/30">
                                <p className="text-xs text-muted-foreground mb-1">Cliente:</p>
                                <p className="font-medium">{message.suggestion.suggested_client.name}</p>
                                <p className="text-xs text-muted-foreground">{message.suggestion.suggested_client.business_type}</p>
                              </div>
                            )}
                            
                            {message.suggestion.suggested_bot && (
                              <div className="p-3 rounded-lg bg-muted/30">
                                <p className="text-xs text-muted-foreground mb-1">Bot:</p>
                                <p className="font-medium">{message.suggestion.suggested_bot.name}</p>
                                <p className="text-xs text-muted-foreground mt-1">{message.suggestion.suggested_bot.welcome_message}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {message.role === 'user' && (
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                        <Sparkles className="w-4 h-4 text-white" />
                      </div>
                      <div className="chat-bubble-ai">
                        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Create from suggestion button */}
              {pendingSuggestion && !isLoading && (
                <div className="px-6 py-3 border-t border-white/10 bg-muted/30">
                  <Button
                    data-testid="create-from-suggestion-btn"
                    onClick={handleCreateFromSuggestion}
                    className="w-full btn-glow"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Crear cliente y bot con esta sugerencia
                  </Button>
                </div>
              )}

              {/* Input area */}
              <div className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <Textarea
                    data-testid="master-ai-input"
                    placeholder="Escribe una instrucción..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="min-h-[48px] max-h-32 resize-none bg-slate-950/50 border border-white/10 rounded-xl text-white placeholder:text-slate-600 focus:ring-0 focus:border-emerald-500/40 focus:shadow-[0_0_20px_rgba(16,185,129,0.15)] transition-all duration-300"
                    rows={1}
                  />
                  <Button
                    data-testid="master-ai-send-btn"
                    onClick={handleSend}
                    disabled={!input.trim() || isLoading}
                    className="btn-glow"
                  >
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar with examples */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-outfit flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Ejemplos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {examplePrompts.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => setInput(prompt)}
                  className="w-full p-3 text-left text-sm rounded-lg border border-white/10 bg-muted/30 hover:bg-muted/50 hover:border-primary/50 transition-all group"
                  data-testid={`example-prompt-${index}`}
                >
                  <p className="text-muted-foreground group-hover:text-foreground transition-colors">
                    {prompt}
                  </p>
                  <ArrowRight className="w-4 h-4 mt-2 text-muted-foreground group-hover:text-primary transition-colors" />
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Quick tips */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg font-outfit">Tips</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>• Especifica el tipo de negocio para mejores resultados</p>
              <p>• Menciona qué flujos necesitas (tickets, citas, pedidos)</p>
              <p>• Puedes editar los bots creados después</p>
              <p>• El agente generará prompts optimizados para tu rubro</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MasterAIPage;
